<template>
  <div>
    <div
      class="subscription-card row"
      :style="{
        borderColor: $theme.primaryColor || '#0564b1',
        color: $theme.darkTheme ? '#fff' : '#333',
      }"
    >
      <div class="subscription-card-header col s7 left">
        <h3 class="subscription-card-title">{{ title }}</h3>
      </div>

      <div class="subscription-card-status col s4">
        <span class="subscription-card-status__ping" :class="statusClass" />
        <span>{{ status }}</span>
      </div>

      <div class="subscription-card-body col s7 left">
        <template v-if="isCancellable">
          <p class="subscription-card-date" v-if="nextBillingDate">
            Renova em: {{ nextBillingDate | formatDate }}
          </p>
          <p class="subscription-card-date" v-if="expirationDate">
            Expira em: {{ expirationDate | formatDate }}
          </p>
        </template>
        <p v-else class="subscription-card-date">
          Expira em: {{ nextBillingDate | formatDate }}
        </p>
      </div>

      <div class="subscription-card-footer col s4">
        <button
          v-if="isCancellable"
          class="subscription-card__cancel-button"
          :style="{ color: $theme.darkTheme ? '#fff' : '#333' }"
          @click="confirmCancelSubscription"
        >
          Cancelar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate } from "@/filters/formatDate";
import ConfirmCancelSubscription from "@/pages/modals/ConfirmCancelSubscription.vue";
import { isMobile } from "mobile-device-detect";

const STATUS = {
  ACTIVE: "Ativa",
  LATE: "Atrasada",
  CANCELLED: "Cancelada",
};

export default {
  name: "SubscriptionCard",
  props: {
    id: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },

    nextBillingDate: {
      type: String,
      required: false,
      default: null,
    },
    expirationDate: {
      type: String,
      required: false,
      default: null,
    },
  },
  computed: {
    isCancellable() {
      return [STATUS.ACTIVE, STATUS.LATE].includes(this.status);
    },
    statusClass() {
      return (
        {
          [STATUS.ACTIVE]: "subscription-card-status__ping--active",
          [STATUS.LATE]: "subscription-card-status__ping--intermediate",
          [STATUS.CANCELLED]: "subscription-card-status__ping--cancelled",
        }[this.status] || "subscription-card-status__ping--intermediate"
      );
    },
  },
  methods: {
    confirmCancelSubscription() {
      const closeConfirmModal = () => {
        this.$modal.hide("ConfirmCancelSubscription");
      };

      const acceptHandler = async () => {
        try {
          await this.$store.dispatch("subscription/cancelSubscription", {
            subscriptionId: this.id,
          });
          this.$store.commit("toast/NEW", {
            type: "success",
            message: "Assinatura cancelada com sucesso.",
          });
        } catch (error) {
          this.$store.commit("toast/NEW", {
            type: "error",
            message:
              "Erro ao cancelar a assinatura. Tente novamente mais tarde.",
            suppressOffline: true,
          });
        }

        closeConfirmModal();
      };

      this.$modal.show(
        ConfirmCancelSubscription,
        {
          title: this.title,
          subscriptionId: this.id,
          nextBillingDate: this.nextBillingDate,
          acceptHandler: acceptHandler,
          rejectHandler: closeConfirmModal,
        },
        {
          name: "ConfirmCancelSubscription",
          width: isMobile ? "95%" : "450px",
          height: "auto",
          transition: "slide-bottom",
        }
      );
    },
  },
  filters: {
    formatDate(date) {
      return formatDate(new Date(date));
    },
  },
};
</script>

<style scoped lang="scss">
.subscription-card {
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  font-size: 14px;

  display: flex;
  flex-wrap: wrap;
  gap: 0px;

  &.row .col {
    padding: 0;
  }

  p {
    margin: 0;
  }

  &.subscription-card--disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .subscription-card-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.875rem;
    flex: 1;

    .subscription-card-status__ping {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;

      &--active {
        background-color: #28a745;
      }
      &--intermediate {
        background-color: #ffc107;
      }
      &--cancelled {
        background-color: #dc3545;
      }
    }
  }

  .subscription-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    margin-left: 0 !important;
  }

  .subscription-card-body {
    margin-left: 0 !important;
  }

  .subscription-card-title {
    font-family: robotobold;
    margin: 0;
    font-size: inherit;
  }

  .subscription-card-date {
    font-size: 0.875rem;
  }

  .subscription-card-footer {
    display: flex;
    margin-top: auto;
    margin-bottom: 3px;
    margin-left: 0 !important;
  }

  .subscription-card__cancel-button {
    font-size: 12px;
    background: none;
    padding: 0;
    margin-left: 12px;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
